import { EvaSeriesKind } from "constants/charts.enums";
import { SeriesOption } from "echarts";
import { BaseLegendStates, MouseSelectionStates, SelectedParams } from "types";
import { ChartActionType, SeriesType } from "types/echarts";
import { EvaChartStates } from "types/factory";
import { allTrue } from "utils/fp";

export const UNDEFINED_DATA_POINT: SelectedParams = {
  targetIndex: undefined,
  seriesIndex: undefined,
  seriesType: SeriesType.None,
  x: undefined,
  y: undefined
};

export const UNDEFINED_HOVER = {
  hoverLegendItem: "",
  hoverLegendGroup: "",
  hoverLegendId: ""
};

function getSeriesIndexFromParams({ params }: Partial<MouseSelectionStates>): number {
  if (
    (params.type === ChartActionType.Hover ||
      params.type === ChartActionType.MouseOver) &&
    typeof params.seriesIndex === "number"
  ) {
    return params.seriesIndex;
  } else if (params.type === ChartActionType.Highlight && Array.isArray(params.batch)) {
    return params.batch[0].seriesIndex;
  } else if (
    params.type === ChartActionType.Highlight &&
    Array.isArray(params.seriesIndex)
  ) {
    return params.seriesIndex[0];
  } else if (
    params.type === ChartActionType.Highlight &&
    typeof params.seriesIndex === "number"
  ) {
    return params.seriesIndex;
  }

  return -1;
}

function isTypeWell(series: SeriesOption & { kind?: EvaSeriesKind }) {
  return "kind" in series && series.kind === EvaSeriesKind.TypeWell;
}

export function getSeriesName({
  params,
  series,
  forecast
}: Partial<MouseSelectionStates>): string {
  const seriesIndex = getSeriesIndexFromParams({ params });
  if (seriesIndex < 0) return "";
  const targetSeries = series[seriesIndex];
  if (!targetSeries) return "";
  const seriesName = targetSeries.name?.toString() ?? "";

  if (forecast) {
    return seriesName.replace(/Forecast.*$/, "").trim();
  }
  return seriesName;
}

export function getSeriesNameUnfiltered({
  params,
  series
}: Partial<MouseSelectionStates>): string {
  const seriesIndex = getSeriesIndexFromParams({ params });
  if (seriesIndex < 0) return "";
  const targetSeries = series[seriesIndex];
  if (!targetSeries) return "";
  const seriesName = targetSeries.name?.toString() ?? "";
  return seriesName;
}

function getSeriesId(series: SeriesOption) {
  const seriesId = (series.id as string) ?? "";
  return seriesId;
}

export function getGroupName({ params, series }: Partial<MouseSelectionStates>): string {
  const seriesIndex = getSeriesIndexFromParams({ params });
  if (seriesIndex < 0) return "";
  const targetSeries = series[seriesIndex];
  if (!targetSeries) return "";
  const seriesId = getSeriesId(targetSeries);
  if (isTypeWell(targetSeries)) return "";
  const match = seriesId.match(/^((?:[^,(]|\([^)]*\))*)/);
  const groupName = (match?.[1] ?? seriesId).trim();
  return groupName;
}

export function getLegendId({ params, series }: Partial<MouseSelectionStates>): string {
  const seriesIndex = getSeriesIndexFromParams({ params });
  if (seriesIndex < 0) return "";
  const targetSeries = series[seriesIndex];
  if (!targetSeries) return "";
  if (!isTypeWell(targetSeries)) return "";
  const seriesId = getSeriesId(targetSeries);
  const typeWellId = seriesId.split(",")[1];

  return typeWellId;
}

export function isValidChartLegendStates({ states }: BaseLegendStates) {
  const { hoverLegendItem, attentionWells, selectedGroups, syncWells } = states;
  const isInvalid = allTrue(
    !hoverLegendItem,
    attentionWells?.length === 0,
    selectedGroups?.length === 0,
    syncWells?.activeWells?.length === 0 || !syncWells?.isSync
  );
  return isInvalid;
}

export function getSeriesIndex({ states, series }: BaseLegendStates) {
  const { hoverLegendItem, hoverLegendId } = states;

  if (hoverLegendId?.length > 0) {
    const seriesIndex = series.findIndex((series) => {
      const seriesId = getSeriesId(series);
      return seriesId.includes(hoverLegendId);
    });
    return [seriesIndex];
  } else {
    return series.reduce((indices: number[], series, index) => {
      const seriesId = getSeriesId(series);
      if (seriesId.includes(hoverLegendItem)) {
        indices.push(index);
      }
      return indices;
    }, []);
  }
}

export function getSeriesIndexFromSelectedGroups({ states, series }: BaseLegendStates) {
  const { selectedGroups } = states;
  if (selectedGroups?.length === 0) return [];
  return series.reduce((indices: number[], series, index) => {
    const seriesId = getSeriesId(series);
    if (selectedGroups.some((group) => seriesId.includes(group))) {
      indices.push(index);
    }
    return indices;
  }, []);
}

export function getAttentionWellIndex({ states, series }: BaseLegendStates) {
  const { attentionWells } = states;

  return series.reduce((indices: number[], series, index) => {
    const seriesId = getSeriesId(series);
    if (seriesId.includes(attentionWells[0])) {
      indices.push(index);
    }
    return indices;
  }, []);
}

/**
 * Locates a well's rendered jitter point by its uwiCoordinates key, stored at row[3]
 * of every jitter data row, instead of a flat positional offset. The Data Range trim
 * hides out-of-range points, so rendered row positions no longer line up with
 * uwiCoordinates indices; matching by key stays correct regardless of filtering.
 * Non-array rows (box data items are objects) are skipped. Returns
 * UNDEFINED_DATA_POINT when the well's point is not rendered, e.g. hidden by the trim.
 */
export function findJitterPointByWellKey(
  options: SeriesOption[],
  wellKey: string
): SelectedParams {
  for (let i = 0; i < options.length; i++) {
    const maybeData = options[i].data;
    if (!Array.isArray(maybeData)) continue;
    for (let j = 0; j < maybeData.length; j++) {
      const row = maybeData[j];
      if (Array.isArray(row) && row[3] === wellKey) {
        return { seriesIndex: i, targetIndex: j };
      }
    }
  }
  return UNDEFINED_DATA_POINT;
}

export function getPointCoordinates(
  states: Partial<EvaChartStates>,
  wellName: string
): { seriesType: SeriesType; x: number; y: number } {
  // Guard: Ensure that uwiCoordinates exists on states and that wellName is present.
  if (!states.uwiCoordinates || !states.uwiCoordinates[wellName]) {
    // If coordinates are missing, return default values (or handle the error as needed)
    return { seriesType: SeriesType.None, x: 0, y: 0 };
  }
  const { x, y } = states.uwiCoordinates[wellName];
  return { seriesType: SeriesType.Scatter, x, y };
}
